import DisclaimerModal from '../../components/apple-ui/DisclaimerModal';
import QuickstartGuide from '../../components/apple-ui/QuickstartGuide';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple } from '@/components/apple-ui';
import {
  FileText,
  Languages,
  PenTool,
  TrendingUp,
  Clock,
  Zap,
  ArrowRight,
  FolderOpen,
  Database,
  BookOpen,
  Plus,
  ExternalLink,
  AlignLeft,
  Video,
  Table2,
  Receipt,
  Download,
  CreditCard
} from 'lucide-react';
import '@/styles/apple-design-system.css';
import { useTheme } from '@/contexts/ThemeContext';
import FeedbackSection from '../../components/FeedbackSection';
import ReferralCTA from '../../components/apple-ui/ReferralCTA';
import ReferralMilestoneCard from '../../components/apple-ui/ReferralMilestoneCard';
import { getUnshownMilestone } from '../../utils/referralTriggers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPageApple = ({ user, planInfo, onLogout }) => {
  const { isDarkMode } = useTheme();
  const [showDisclaimer, setShowDisclaimer] = React.useState(false);
  const [showQuickstart, setShowQuickstart] = React.useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/quickstart', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      if (!d.disclaimer_accepted) {
        setShowDisclaimer(true);
      } else if (!d.dismissed) {
        setShowQuickstart(true);
      }
    }).catch(() => {});
  }, []);
  const [stats, setStats] = useState({
    totalWords: 0,
    projectsCount: 0,
    tmEntries: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [activeMilestone, setActiveMilestone] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/user/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices((data.invoices || []).slice(0, 3));
      }
    } catch {}
    finally { setInvoicesLoading(false); }
  };

  const downloadInvoice = async (invoiceNumber) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/user/invoices/${invoiceNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const formatInvoiceDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    try {
      const projectsRes = await fetch(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const projectsRaw = projectsRes.ok ? await projectsRes.json() : [];
      const projects = Array.isArray(projectsRaw) ? projectsRaw : (Array.isArray(projectsRaw.projects) ? projectsRaw.projects : []);

      const usageRes = await fetch(`${API_URL}/api/pricing/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const usageData = usageRes.ok ? await usageRes.json() : {};

      // Count TM entries
      let tmCount = 0;
      try {
        const tmRes = await fetch(`${API_URL}/api/translation-memory/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (tmRes.ok) {
          const tmData = await tmRes.json();
          tmCount = tmData.tm_entries || 0;
        }
      } catch {}

      const monthlyUsage = usageData.monthly_usage || 0;
      setStats({
        totalWords: monthlyUsage,
        projectsCount: projects.length || 0,
        tmEntries: tmCount,
        wordsThisMonth: monthlyUsage,
        wordsRemaining: usageData.words_remaining || 0,
        usagePercent: usageData.usage_percent || 0,
        savings: usageData.credits_saved || 0
      });
      const milestone = getUnshownMilestone(monthlyUsage);
      if (milestone) setActiveMilestone(milestone);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: FileText, title: 'CAT Tool', description: 'Professional translation editor with TM & glossary', href: '/cat', color: 'bg-blue-500', badge: 'PRO' },
    { icon: Languages, title: 'Basic Translation', description: 'Quick text translation with formatting', href: '/translation', color: 'bg-green-500' },
    { icon: PenTool, title: 'MTPE', description: 'Machine translation post-editing', href: '/mtpe', color: 'bg-purple-500' },
    { icon: FolderOpen, title: 'Projects', description: 'Manage your translation projects', href: '/projects', color: 'bg-amber-500' }
  ];

  const statCards = [
    { label: 'Words Translated', value: stats.totalWords?.toLocaleString() || '0', icon: TrendingUp, color: 'text-blue-600', bgColor: 'bg-blue-50', darkBgColor: 'bg-blue-900/30' },
    { label: 'Active Projects', value: stats.projectsCount?.toString() || '0', icon: FolderOpen, color: 'text-amber-600', bgColor: 'bg-amber-50', darkBgColor: 'bg-amber-900/30' },
    { label: 'TM Entries', value: stats.tmEntries?.toLocaleString() || '0', icon: Database, color: 'text-purple-600', bgColor: 'bg-purple-50', darkBgColor: 'bg-purple-900/30' },
    { label: 'Words This Month', value: stats.wordsThisMonth?.toLocaleString() || '0', icon: Clock, color: 'text-green-600', bgColor: 'bg-green-50', darkBgColor: 'bg-green-900/30' }
  ];

  return (
    <>
    {showDisclaimer && <DisclaimerModal onAccepted={() => { setShowDisclaimer(false); setShowQuickstart(true); }} />}(
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-dashboard">
        <div className="apple-content-header">
          <h1 className="apple-content-title">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : user?.email ? `, ${user.email.split('@')[0]}` : ''}
          </h1>
          <p className="apple-content-subtitle">
            Here's an overview of your translation workspace
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {statCards.map((stat, idx) => (
            <CardApple key={idx} className="p-5" data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1 font-medium">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 tracking-tight">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${isDarkMode ? stat.darkBgColor : stat.bgColor}`}>
                  <stat.icon size={22} className={stat.color} />
                </div>
              </div>
            </CardApple>
          ))}
        </div>

        <ReferralMilestoneCard
          milestone={activeMilestone}
          onDismiss={() => setActiveMilestone(null)}
        />

        <div className="mb-10">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {quickActions.map((action, idx) => (
              <Link key={idx} to={action.href} className="group" data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardApple className="p-5 h-full transition-all group-hover:-translate-y-1">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${action.color} text-white shadow-lg`}>
                      <action.icon size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{action.title}</h3>
                        {action.badge && <BadgeApple variant="blue" size="sm">{action.badge}</BadgeApple>}
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{action.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-sm text-blue-600 font-semibold group-hover:translate-x-1 transition-transform">
                    Get started <ArrowRight size={16} className="ml-1" />
                  </div>
                </CardApple>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CardApple>
            <CardApple.Header actions={<Link to="/projects" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="view-all-projects-link">View all</Link>}>
              <CardApple.Title>Recent Projects</CardApple.Title>
            </CardApple.Header>
            <CardApple.Body>
              {stats.projectsCount === 0 ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="text-gray-400" size={28} />
                  </div>
                  <p className="text-gray-500 mb-4">No projects yet</p>
                  <Link to="/projects">
                    <ButtonApple icon={Plus} size="sm" data-testid="create-first-project-btn">Create your first project</ButtonApple>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-600 mb-4">You have <span className="font-semibold">{stats.projectsCount}</span> project(s)</p>
                  <Link to="/projects"><ButtonApple variant="secondary" size="sm">View Projects</ButtonApple></Link>
                </div>
              )}
            </CardApple.Body>
          </CardApple>

          <CardApple>
            <CardApple.Header><CardApple.Title>Resources</CardApple.Title></CardApple.Header>
            <CardApple.Body className="space-y-2">
              <Link to="/tm" className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors" data-testid="resource-tm-link">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}><Database size={20} className="text-purple-600" /></div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Translation Memory</p>
                  <p className="text-sm text-gray-500">{stats.tmEntries || 0} entries saved</p>
                </div>
                <ArrowRight size={18} className="text-gray-400" />
              </Link>
              
              <Link to="/glossary" className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors" data-testid="resource-glossary-link">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}><BookOpen size={20} className="text-green-600" /></div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Glossary</p>
                  <p className="text-sm text-gray-500">Manage terminology</p>
                </div>
                <ArrowRight size={18} className="text-gray-400" />
              </Link>

              <div className={`flex items-center gap-4 p-4 rounded-xl border ${isDarkMode ? 'bg-[#1e2a3a] border-[#2a3f5f]' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#1c3a6e]' : 'bg-blue-100'}`}><Zap size={20} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} /></div>
                <div className="flex-1">
                  <p className={`font-semibold ${isDarkMode ? 'text-blue-300' : 'text-gray-900'}`}>Pro Tip</p>
                  <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-gray-600'}`}>TM matches are free and instant. Build your TM to save costs!</p>
                </div>
              </div>
            </CardApple.Body>
          </CardApple>
        </div>

        {planInfo && planInfo.plan === 'free' && (
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white shadow-xl shadow-blue-600/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-xl mb-1">Upgrade to Pro</h3>
                <p className="text-blue-100 text-sm">Get unlimited translations, advanced TM, and team collaboration features.</p>
              </div>
              <Link to="/pricing" className="apple-btn bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-2.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5" data-testid="upgrade-banner-btn">View Plans</Link>
            </div>
          </div>
        )}
        <ReferralCTA />

        {/* Translator Tools */}
        <div className="mt-10 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Translator Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
            {[
              {
                icon: AlignLeft,
                name: 'Word Counter',
                href: 'https://wordcounter.net/',
                tag: 'Productivity',
                tagColor: 'bg-blue-100 text-blue-700',
                desc: 'Count words, characters, and reading time for any text.',
              },
              {
                icon: FileText,
                name: 'PDF Tools',
                href: 'https://www.ilovepdf.com/',
                tag: 'File Conversion',
                tagColor: 'bg-red-100 text-red-700',
                desc: 'Merge, split, compress, and convert PDF files online.',
              },
              {
                icon: Video,
                name: 'Subtitle Tools',
                href: 'https://subtitletools.com/',
                tag: 'Media',
                tagColor: 'bg-purple-100 text-purple-700',
                desc: 'Edit, convert, sync, and translate subtitle files.',
              },
              {
                icon: Table2,
                name: 'Excel to TMX',
                href: 'https://translatum.gr/cgi-bin/excel-to-tmx.pl',
                tag: 'CAT Tools',
                tagColor: 'bg-green-100 text-green-700',
                desc: 'Convert Excel bilingual files into TMX translation memory.',
              },
            ].map((tool, idx) => (
              <a
                key={idx}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
              >
                <CardApple className="p-5 h-full transition-all group-hover:-translate-y-1">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-gray-100 text-gray-600 group-hover:bg-gray-200 transition-colors">
                      <tool.icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{tool.name}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tool.tagColor}`}>{tool.tag}</span>
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">{tool.desc}</p>
                    </div>
                    <ExternalLink size={16} className="text-gray-400 mt-1 flex-shrink-0 group-hover:text-blue-500 transition-colors" />
                  </div>
                </CardApple>
              </a>
            ))}
          </div>
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9888;</span>
            <p className="text-sm text-amber-800">
              These tools are suggested for your convenience only. t09n.com is not affiliated with or responsible for any damage or loss from using these third-party tools.
            </p>
          </div>
        </div>

        {/* Billing & Invoices */}
        <div className="mt-8 mb-8">
          <CardApple>
            <CardApple.Header actions={
              <Link to="/billing" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View all <ArrowRight size={14} />
              </Link>
            }>
              <CardApple.Title>
                <span className="flex items-center gap-2"><Receipt size={18} className="text-blue-600" />Billing &amp; Invoices</span>
              </CardApple.Title>
            </CardApple.Header>
            <CardApple.Body>
              {planInfo && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gray-50">
                  <div className="p-2 rounded-lg bg-blue-50"><CreditCard size={16} className="text-blue-600" /></div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Current Plan</p>
                    <p className="font-semibold text-gray-900 capitalize">{planInfo.plan || 'Free'}</p>
                  </div>
                </div>
              )}

              {invoicesLoading ? (
                <p className="text-sm text-gray-400 py-4 text-center">Loading invoices…</p>
              ) : invoices.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No invoices yet — invoices are generated after payment.</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {invoices.map((inv) => (
                    <div key={inv.invoice_number} className="flex items-center gap-3 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{inv.invoice_number}</p>
                        <p className="text-xs text-gray-500 truncate">{inv.description} · {formatInvoiceDate(inv.created_at)}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-800 shrink-0">₹{(inv.gross_amount || 0).toLocaleString()}</p>
                      <button
                        onClick={() => downloadInvoice(inv.invoice_number)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors shrink-0"
                        title="Download PDF"
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardApple.Body>
          </CardApple>
        </div>

      <FeedbackSection />
      </div>
    </MainLayoutApple>
    </>
  );
};

export default DashboardPageApple;